import {Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Tooltip} from "@mui/material";
import TextFieldValidation from "../../../../components/UI/table/customer-detales/TextFieldValidation";
import ButtonExit from "../../../../components/UI/button/ButtonExit";
import ButtonAdd from "../../../../components/UI/button/ButtonAdd";
import React, {useEffect, useState} from "react";
import {CUSTOMER_PASSPORT_FIELD_DEFAULTS, CUSTOMER_PASSPORT_FIELD_KEYS, personalization} from "../../../../data/Personalization";
import OrderDatePicker from "../../../../components/UI/datepicker/OrderDatePicker";
import dayjs from "dayjs";
import MenuItem from "@mui/material/MenuItem";
import {Box, TextField} from "@mui/material";
import AddressCustomerService from "../../../../API/customer/AddressCustomerService";
import AddressOrganizationService from "../../../../API/organization/AddressOrganizationService";
import FormControlLabel from "@mui/material/FormControlLabel";
import {formatAddressOptionLabel} from "../../../../utils/customerAddressUtils";
import {
    buildPassportPayload,
    sanitizePassportInput,
    validatePassportString,
} from "../../../../utils/customerPassportUtils";

const EXCLUDED_FIELDS = new Set(["presenceCloud", "organizationSettings", "id", "enabled"]);

const isAddressIdInList = (value, addressData) =>
    value !== "" && addressData.some((item) => String(item.id) === String(value));

const getOrderedFieldKeys = (formState, organization, hiddenFields) => {
    const visibleKeys = Object.keys(formState).filter((name) =>
        !EXCLUDED_FIELDS.has(name)
        && !hiddenFields.includes(name)
        && !(organization && CUSTOMER_PASSPORT_FIELD_KEYS.includes(name))
    );

    if (organization) {
        return visibleKeys;
    }

    const passportKeys = CUSTOMER_PASSPORT_FIELD_KEYS.filter((key) => visibleKeys.includes(key));
    const otherKeys = visibleKeys.filter((key) => !CUSTOMER_PASSPORT_FIELD_KEYS.includes(key));

    return [...otherKeys, ...passportKeys];
};

const PersonalizationModal = ({
    open,
    onClose,
    handleSubmit,
    initValue,
    handleModalAlert,
    organization,
    generalSettings,
    loading,
    hiddenFields = [],
    requireAddressFields = false,
    addressRefreshKey = 0,
}) => {
    const [formState, setFormState] = useState({"residentialAddress": "", "address": ""})
    const [validationState, setValidationState] = useState({})
    const [addressData, setAddressData] = useState([])
    const [addressDataLoaded, setAddressDataLoaded] = useState(false)

    const buildEmptyValidationState = (state) =>
        Object.keys(state).reduce((acc, key) => ({...acc, [key]: ""}), {});

    useEffect(() => {
        const nextFormState = organization ? initValue : {...CUSTOMER_PASSPORT_FIELD_DEFAULTS, ...initValue};
        if (!organization && nextFormState.passport) {
            nextFormState.passport = buildPassportPayload(nextFormState.passport);
        }
        setFormState(nextFormState);
        const nextErrors = buildEmptyValidationState(nextFormState);
        if (!organization && nextFormState.passport) {
            nextErrors.passport = validatePassportString(nextFormState.passport);
        }
        setValidationState(nextErrors);
    }, [initValue, organization]);

    useEffect(() => {
        if (!open) {
            setAddressData([])
            setAddressDataLoaded(false)
            return
        }

        let cancelled = false
        setAddressDataLoaded(false)
        const api = organization ? AddressOrganizationService : AddressCustomerService
        api.getAll().then(function (response) {
            if (!cancelled) {
                setAddressData(response.data);
                setAddressDataLoaded(true)
            }
        })
            .catch(function (error) {
                if (!cancelled) {
                    handleModalAlert(error);
                }
            });

        return () => {
            cancelled = true
        }
    }, [open, addressRefreshKey, organization]);

    useEffect(() => {
        if (!open || !addressDataLoaded) {
            return;
        }
        setFormState((prev) => ({
            ...prev,
            address: isAddressIdInList(prev.address, addressData) ? prev.address : "",
            residentialAddress: isAddressIdInList(prev.residentialAddress, addressData) ? prev.residentialAddress : "",
        }));
    }, [open, addressDataLoaded, addressData]);

    const handleClose = () => {
        onClose()
        const resetState = organization ? initValue : {...CUSTOMER_PASSPORT_FIELD_DEFAULTS, ...initValue};
        if (!organization && resetState.passport) {
            resetState.passport = buildPassportPayload(resetState.passport);
        }
        setFormState(resetState);
        setValidationState(buildEmptyValidationState(resetState));
    }

    const handlePassportChange = (event) => {
        const sanitized = sanitizePassportInput(event.target.value);
        setFormState((prev) => ({...prev, passport: sanitized}));
        setValidationState((prev) => ({
            ...prev,
            passport: sanitized ? validatePassportString(sanitized) : "",
        }));
    };

    const handleValidationData = () => {

        const newErrors = {...validationState}
        Object.entries(formState).forEach(([name, value]) => {
            if (hiddenFields.includes(name) || name === "passport" || CUSTOMER_PASSPORT_FIELD_KEYS.includes(name)) {
                return
            }
            if (name === "address" || name === "residentialAddress") {
                if (value !== "" && !isAddressIdInList(value, addressData)) {
                    newErrors[name] = "адрес не найден"
                } else if (requireAddressFields && value === "") {
                    newErrors[name] = "пустое поле"
                }
                return
            }
            if (value === "") {
                newErrors[name] = "пустое поле"
            }
        })

        if (!organization) {
            const passportError = validatePassportString(formState.passport);
            if (passportError) {
                newErrors.passport = passportError;
            }
        }

        setValidationState(newErrors)
        if (Object.values(newErrors).every(el => el === "")) {
            const payload = organization
                ? formState
                : {
                    ...formState,
                    passport: buildPassportPayload(formState.passport),
                };
            handleSubmit(payload, handleClose)
        }
    }

    const renderField = (name, value) => {
        if (name === "passport") {
            return (
                <Box key={name} sx={{minHeight: 70}}>
                    <TextField
                        autoComplete="off"
                        name={name}
                        label={personalization.passport}
                        size="small"
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        value={value || ""}
                        onChange={handlePassportChange}
                        error={Boolean(validationState.passport)}
                        helperText={validationState.passport || ""}
                        InputLabelProps={{shrink: true}}
                        sx={{
                            width: "100%",
                            "& .MuiFormHelperText-root": {
                                marginTop: "2px",
                                marginBottom: 0,
                                lineHeight: 1.2,
                            },
                        }}
                    />
                </Box>
            );
        }

        if (name === "birthday" || name === "dateOfIssuePassport") {
            return <OrderDatePicker
                key={name}
                label={personalization[name]}
                date={value || ""}
                setDate={(res) => setFormState((prev) => ({
                    ...prev,
                    [name]: dayjs(res).format('YYYY-MM-DD')
                }))}/>
        }

        if (name === "residentialAddress" || name === "address") {
            return <TextFieldValidation
                select
                key={name}
                name={name}
                value={value}
                disabled={!addressDataLoaded}
                label={organization && name === "residentialAddress" ? "Юридический адрес" : personalization[name]}
                setCurrValues={setFormState}
                validationError={validationState[name]}
                setValidationState={setValidationState}
            >
                {!addressDataLoaded && (
                    <MenuItem disabled value="">
                        Загрузка...
                    </MenuItem>
                )}
                {addressDataLoaded && addressData.length === 0 && (
                    <MenuItem disabled value="">
                        Нет адресов
                    </MenuItem>
                )}
                {addressData.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                        {formatAddressOptionLabel(option)}
                    </MenuItem>))}
            </TextFieldValidation>
        }

        return <TextFieldValidation
            key={name}
            name={name}
            type={name === "phoneNumber" ? "tel" : "text"}
            value={value || ""}
            disabled={name === "position"}
            label={personalization[name]}
            setCurrValues={setFormState}
            required={false}
            validationError={validationState[name]}
            setValidationState={setValidationState}
        />
    }

    const orderedFieldKeys = getOrderedFieldKeys(formState, organization, hiddenFields);

    return (
        <Dialog open={open} PaperProps={{
            style: {
                minWidth: "400px",
                borderRadius: 8,
            }
        }}>
            <DialogTitle textAlign="center">Изменить</DialogTitle>
            <DialogContent sx={{padding: "13px 24px", minWidth: "400px"}}>
                {orderedFieldKeys.map((name) => renderField(name, formState[name]))}
                {organization && <FormControlLabel
                    control={<Checkbox
                        onChange={() => setFormState(prev => ({
                            ...prev,
                            presenceCloud: !prev.presenceCloud
                        }))}
                        checked={formState?.presenceCloud}
                    />}
                    label="Участие в портале"
                />}
            </DialogContent>
            <DialogActions sx={{p: '1.25rem'}}>
                <ButtonExit text={"Отменить"} onClick={handleClose}/>
                <ButtonAdd loading={loading} text={"Изменить"} onClick={handleValidationData}/>
            </DialogActions>
        </Dialog>
    );
}
export default PersonalizationModal